import { useState } from "react";
import { toast } from "sonner";
import {
  Check, Circle, ExternalLink, Globe, Loader2, ShieldCheck, ChevronDown, ChevronUp, AlertCircle,
} from "lucide-react";
import { api } from "../lib/api";

const REGISTRAR_LINKS = [
  { name: "Cloudflare Registrar", url: "https://www.cloudflare.com/products/registrar/" },
  { name: "Namecheap", url: "https://www.namecheap.com/" },
  { name: "Porkbun", url: "https://porkbun.com/" },
];

export default function CustomDomainSteps({ deployment, onUpdated }) {
  const [expanded, setExpanded] = useState(false);
  const [domain, setDomain] = useState(deployment?.custom_domain || "");
  const [busy, setBusy] = useState(false);
  const checks = deployment?.custom_domain_checks || [];
  const isLive = deployment?.custom_domain_ok;

  const check = async () => {
    if (!domain.trim()) return toast.error("Enter your domain, e.g. yourdomain.com");
    setBusy(true);
    try {
      const { data } = await api.post(`/website/deployments/${deployment.id}/check-domain`, { domain: domain.trim() });
      if (data.custom_domain_ok) toast.success("Domain is live 🎉");
      else toast.warning("DNS isn't reachable yet — could take up to a few hours to propagate.");
      onUpdated && onUpdated(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not check domain.");
    } finally { setBusy(false); }
  };

  const steps = [
    { title: "Register your domain", body: <>Pick and buy a domain. Any registrar works — these are known-friendly:{" "}
        {REGISTRAR_LINKS.map((r, i) => (
          <span key={r.url}>
            {i > 0 ? " · " : " "}
            <a href={r.url} target="_blank" rel="noopener noreferrer" className="underline hover:text-marigold">{r.name}</a>
          </span>
        ))}</>, done: !!deployment?.custom_domain },
    { title: "Add domain in Vercel", body: <>Open your project in Vercel &rarr; <strong>Settings &rarr; Domains</strong> &rarr; paste your domain. Vercel will show DNS records to copy.</>, done: !!deployment?.custom_domain },
    { title: "Paste DNS records at registrar", body: <>At your registrar, add: an <strong>A record</strong> for <code className="text-[10px] bg-paper px-1 rounded">@</code> pointing to <code className="text-[10px] bg-paper px-1 rounded">76.76.21.21</code>, and a <strong>CNAME</strong> for <code className="text-[10px] bg-paper px-1 rounded">www</code> pointing to <code className="text-[10px] bg-paper px-1 rounded">cname.vercel-dns.com</code>. DNS can take 5&ndash;30 minutes to propagate.</>, done: !!deployment?.custom_domain },
    { title: "Verify below", body: <>Paste your domain and click <strong>Check</strong>. We&apos;ll ping both the apex and <code className="text-[10px] bg-paper px-1 rounded">www</code> variants and confirm it&apos;s live.</>, done: isLive },
  ];

  const completed = steps.filter((s) => s.done).length;

  return (
    <div className="rounded-xl border border-border bg-cream" data-testid={`custom-domain-${deployment.id}`}>
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-2 p-3 text-left hover:bg-paper/50 transition rounded-t-xl"
        data-testid={`custom-domain-toggle-${deployment.id}`}
      >
        <Globe className="w-4 h-4 text-teal" />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold text-ink">Custom domain</div>
          <div className="text-[10px] text-ink2">
            {isLive
              ? `Live at ${deployment.custom_domain}`
              : deployment?.custom_domain
                ? `${deployment.custom_domain} — not reachable yet`
                : "Bring your own domain in 4 steps"}
          </div>
        </div>
        <span className="text-[10px] font-semibold text-ink2 mr-1">{completed}/4</span>
        {expanded ? <ChevronUp className="w-3.5 h-3.5 text-ink2" /> : <ChevronDown className="w-3.5 h-3.5 text-ink2" />}
      </button>

      {expanded && (
        <div className="p-3 border-t border-border space-y-3" data-testid={`custom-domain-body-${deployment.id}`}>
          <ol className="space-y-2.5">
            {steps.map((s, i) => (
              <li key={i} className="flex gap-2 items-start" data-testid={`custom-domain-step-${i}`}>
                {s.done
                  ? <Check className="w-4 h-4 text-teal flex-shrink-0 mt-0.5" />
                  : <Circle className="w-4 h-4 text-ink2/50 flex-shrink-0 mt-0.5" />}
                <div className="flex-1 min-w-0">
                  <div className={`text-xs font-semibold ${s.done ? "text-ink2 line-through" : "text-ink"}`}>
                    {i + 1}. {s.title}
                  </div>
                  <div className="text-[11px] text-ink2 leading-relaxed mt-0.5">{s.body}</div>
                </div>
              </li>
            ))}
          </ol>

          <div className="pt-2 border-t border-border/60">
            <label className="text-[10px] uppercase tracking-wider text-ink2 font-semibold block mb-1">Your domain</label>
            <div className="flex gap-1.5">
              <input
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="yourdomain.com"
                className="flex-1 bg-paper border border-border rounded-lg px-2 py-1.5 text-xs outline-none focus:border-teal"
                data-testid={`custom-domain-input-${deployment.id}`}
              />
              <button
                onClick={check}
                disabled={busy || !domain.trim()}
                className="text-xs px-3 py-1.5 rounded-lg bg-teal text-cream disabled:opacity-40 hover:opacity-90 transition"
                data-testid={`custom-domain-check-${deployment.id}`}
              >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Check"}
              </button>
            </div>

            {checks.length > 0 && (
              <div className="mt-2 space-y-1" data-testid={`custom-domain-results-${deployment.id}`}>
                {checks.map((c) => (
                  <div key={c.host} className="flex items-center gap-1.5 text-[11px]">
                    {c.reachable
                      ? <ShieldCheck className="w-3 h-3 text-teal" />
                      : <AlertCircle className="w-3 h-3 text-marigold" />}
                    <span className="font-mono text-ink">{c.host}</span>
                    <span className={c.reachable ? "text-teal" : "text-ink2"}>
                      {c.reachable
                        ? `reachable (${c.status_code})`
                        : c.status_code ? `HTTP ${c.status_code}` : "no response"}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {isLive && (
              <a
                href={`https://${deployment.custom_domain}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-1 text-xs text-teal font-semibold hover:underline"
                data-testid={`custom-domain-live-${deployment.id}`}
              >
                Visit {deployment.custom_domain} <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
